/**
 * Redux store configuration
 * @author Maheen Nasir
 * @description Sets up the Redux store with initial reducer configuration
 */

import { configureStore, combineReducers } from "@reduxjs/toolkit";
import configReducer from "./slices/configSlice";
import userReducer from "./slices/userSlice";
import courseReducer from "./slices/courseSlice";
import courseDetailsReducer from "./slices/courseDetailsSlice";
import notificationReducer from "./slices/notificationSlice";
import instituteReducer from "./slices/instituteSlice";
import applicationsReducer from "./slices/applicationsSlice";
import signupReducer from "./slices/signupSlice";
import dashboardCoursesReducer from "./slices/dashboardCoursesSlice";
import coursePreferencesReducer from "./slices/coursePreferencesSlice";
import modalReducer from "./slices/modalSlice";
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import storage from "redux-persist/lib/storage";
import type { ConfigState } from "./slices/configSlice";
import type { SignupState } from "./slices/types";
import type { DashboardCoursesState } from "./slices/dashboardCoursesSlice";
import filterModalReducer from "./slices/filterModalSlice";

/**
 * Configured Redux store instance
 * @description Creates and configures the Redux store with initial reducers
 */

const configPersistConfig = {
	key: "config",
	storage,
	whitelist: ["selectedMenuItem", "challanId", "challanAmount", "applicationStatus", "applicationId"],
};

const signupPersistConfig = {
	key: "signup",
	storage,
	blacklist: ["passwordData"],
};

const dashboardCoursesPersistConfig = {
	key: "dashboardCourses",
	storage,
	whitelist: ["courses", "filteredCourses", "hasFilteredResults", "isPreference", "page", "hasNextPage", "filteredPage", "filteredLimit", "filteredHasNextPage", "filteredTotalItems", "storedFilterParams"],
};

const rootReducer = combineReducers({
	config: persistReducer<ConfigState>(configPersistConfig, configReducer),
	signup: persistReducer<SignupState>(signupPersistConfig, signupReducer),
	user: userReducer,
	course: courseReducer,
	courseDetails: courseDetailsReducer,
	notifications: notificationReducer,
	institute: instituteReducer,
	applications: applicationsReducer,
	dashboardCourses: persistReducer<DashboardCoursesState>(dashboardCoursesPersistConfig, dashboardCoursesReducer),
	coursePreferences: coursePreferencesReducer,
	filterModal: filterModalReducer,
	modal: modalReducer,
});

export const store = configureStore({
	reducer: rootReducer,
	middleware: getDefaultMiddleware =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
			},
		}),
});

export const persistor = persistStore(store);

/** Type definition for the root state */
export type RootState = ReturnType<typeof store.getState>;
/** Type definition for the dispatch function */
export type AppDispatch = typeof store.dispatch;
